const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/reducers/patientsReducer.ts';
import Imm from 'immutable';
import { KisLoggerService } from '../services/KisLoggerService';
import * as PatActions from '../actions/patientsActions';
import {
    atSetCurrentPatNr,
    atSetCurrentPatNrFallNr,
    DeletePatientPictureAction,
    LOAD_PATIENT,
    SetCurrentPatNrAction,
    SetCurrentPatNrFallNrAction,
    UploadPatientPictureAction,
} from '../actions/patientsActions';
import { decCounter, incCounter } from '../utils/reducerUtils';
import {
    defaultKisApiActionFailure,
    defaultKisApiActionPending,
    defaultKisApiActionSuccess,
} from '@cistec/common_web/utils/redux4Requests';

const Log = KisLoggerService.getSingleton();
const logChannel = 'common/patientsReducer';

/**
 * The initial values of this reducer's branch (common values only).
 */
export const initialState = Imm.fromJS({
    currentPatient: null, // patnr of currently selected patient
    patients: {}, // key: patnr, value: patient record
});

/**
 * The actual reducer
 */
export function patientsReducer(state = initialState, action: any = {}) {
    switch (action.type) {
        case atSetCurrentPatNr:
            return state.set('currentPatient', (action as SetCurrentPatNrAction).payload);
        case atSetCurrentPatNrFallNr:
            return state.set('currentPatient', (action as SetCurrentPatNrFallNrAction).patnr);

        case LOAD_PATIENT.pending:
            return state.setIn(['loading'], true);
        case LOAD_PATIENT.success:
            return state.withMutations((mutState: any) => {
                mutState.setIn(['loading'], false);
                const entry = action.payload;
                addPatient(mutState, entry, action.meta.fullRecord, action.meta.caseId);
            });
        case LOAD_PATIENT.failure:
            return state.setIn(['loading'], false);

        case PatActions.LOAD_PATIENT_PICTURE.pending:
            return loadPatientPicturePending(state, action);
        case PatActions.LOAD_PATIENT_PICTURE.success:
            return loadPatientPictureSuccess(state, action);
        case PatActions.LOAD_PATIENT_PICTURE.failure:
            return loadPatientPictureFailure(state, action);

        case PatActions.UPLOAD_PATIENT_PICTURE.pending:
            return uploadPatientPicturePending(state, action);
        case PatActions.UPLOAD_PATIENT_PICTURE.success:
            return uploadPatientPictureSuccess(state, action);
        case PatActions.UPLOAD_PATIENT_PICTURE.failure:
            return uploadPatientPicturefailure(state, action);

        case PatActions.DELETE_PATIENT_PICTURE.success:
            return deletePatientPictureSuccess(state, action);

        default:
            return state;
    }
}

function addPatient(mutableState: Imm.Map<string, any>, rec: any, fullRecord: boolean = false, caseId?: string) {
    // NOTE: rec comes from KISAPI and may contain data that's not
    // supposed to be put in the patient store. (Depending on the
    // endpoint that was used, the reply may contain information
    // from Encounter, Termin, Fall or MetaKat.) Each reducer should
    // pick whatever belongs into this branch of the store from the
    // reply. Because of that, the data is picked explicitly.

    // rec depends on which endpoint is used to retrieve the data. When the data is loaded with .../Patients?PATNR={patnr}... (instead of
    // .../Patients/{patnr}) then rec has to be adjusted here.
    if (rec.hasOwnProperty('Info') && rec.hasOwnProperty('List') && Array.isArray(rec.List)) {
        rec = rec.List[0];
    }
    const patientContent = rec?.Std ?? rec;
    const specificContent = rec?.Specific ?? undefined;
    if (fullRecord) {
        mutableState.setIn(
            ['patients', patientContent.PATNR],
            Imm.fromJS({
                PATNR: patientContent.PATNR || '',
                TITEL: patientContent.TITEL || '',
                NAME: patientContent.NAME || '',
                VORNAME: patientContent.VORNAME || '',
                // this should prevent moment from just creating a valid date for today in case of an undefined value returned by the API
                GEBDAT: patientContent.GEBDAT || null,
                SEX: patientContent.SEX,
                STATUS: patientContent.STATUS,
                VERSTORBEN: patientContent.VERSTORBEN,
                TODESDATUM: patientContent.TODESDATUM,
                VIP: patientContent.VIP,
                MITARBEITER: patientContent.MITARBEITER,
                SCHLECHTER_ZAHLER: patientContent.SCHLECHTER_ZAHLER,
                GEBURTSNAME: patientContent.GEBURTSNAME,
                ZIVILSTAND: patientContent.ZIVILSTAND,
                KONFESSION: patientContent.KONFESSION,
                SPRACHE: patientContent.SPRACHE,
                BERUF: patientContent.BERUF,
                NATIONALITAET: patientContent.NATIONALITAET,
                REGION: patientContent.REGION,
                SOZVERSNR: patientContent.SOZVERSNR,
                HEIMATORT: patientContent.HEIMATORT,
                HINWEISE: patientContent.HINWEISE,
                BEMERKUNG: patientContent.BEMERKUNG,
                ANREDE: patientContent.ANREDE,
                FACHTITEL: patientContent.FACHTITEL,
                ANSCHR2: patientContent.ANSCHR2,
                STRASSE: patientContent.STRASSE,
                ADRESSZUSATZ: patientContent.ADRESSZUSATZ,
                PLZ: patientContent.PLZ,
                ORT: patientContent.ORT,
                LAND: patientContent.LAND,
                EMAIL: patientContent.EMAIL,
                TELEFON: patientContent.TELEFON,
                TELEFON_G: patientContent.TELEFON_G,
                TELEFON_M: patientContent.TELEFON_M,
                FAX: patientContent.FAX,
                VIS: patientContent.VIS,
                DAT: patientContent.DAT,
                MUTVIS: patientContent.MUTVIS,
                MUTDAT: patientContent.MUTDAT,
                GENCONS: specificContent?.GENCONS || undefined,
                Extras: specificContent?.Extras,
            })
        );
    } else {
        mutableState.setIn(
            ['patients', patientContent.PATNR],
            Imm.fromJS({
                PATNR: patientContent.PATNR || '',
                TITEL: patientContent.TITEL || '',
                NAME: patientContent.NAME || '',
                VORNAME: patientContent.VORNAME || '',
                GEBDAT: patientContent.GEBDAT || null,
                SEX: patientContent.SEX,
                STATUS: patientContent.STATUS,
                VERSTORBEN: patientContent.VERSTORBEN,
                TODESDATUM: patientContent.TODESDATUM,
                GENCONS: specificContent?.GENCONS || undefined,
                Extras: specificContent?.Extras,
            })
        );
    }

    // reference people are case specific and are stored separately for each case
    if (caseId) {
        mutableState.setIn(
            ['patients', patientContent.PATNR, 'caseSpecific', caseId],
            Imm.fromJS({
                Bezugspersonen: patientContent.Bezugspersonen,
            })
        );
    }
}

function loadPatientPicturePending(state: Imm.Map<string, any>, action: PatActions.ILoadPatientPictureAction) {
    const patnr = action.meta.patnr;

    state = defaultKisApiActionPending(state, ['avatars', patnr], action);

    return incCounter(state, ['images', patnr, 'loading']);
}

function loadPatientPictureSuccess(state: Imm.Map<string, any>, action: PatActions.ILoadPatientPictureAction) {
    const patnr = action.meta.patnr;

    state = defaultKisApiActionSuccess(state, `see state.patients.images.${patnr}.data`, ['avatars', patnr]);

    const list = Imm.fromJS(action.payload?.List || {});
    const entry = list.first();
    let data: unknown = '';

    if (entry && Imm.Iterable.isIterable(entry)) {
        data = entry.get('CONTENT');
    }

    if (typeof data !== 'string' || !data) {
        data = 'failed'; // mark it as failed, so we won't try to load it again
    }

    state = state.setIn(['images', patnr, 'data'], data);
    return decCounter(state, ['images', patnr, 'loading']);
}

function loadPatientPictureFailure(state: Imm.Map<string, any>, action: PatActions.ILoadPatientPictureAction) {
    Log.warn(logChannel, 'Failed to load patient picture', action);
    const patnr = action.meta.patnr;

    state = defaultKisApiActionFailure(state, ['avatars', patnr], action);

    state = state.setIn(['images', patnr, 'data'], 'failed');
    return decCounter(state, ['images', patnr, 'loading']);
}

function uploadPatientPicturePending(state: Imm.Map<string, any>, action: UploadPatientPictureAction) {
    const patnr = action.meta.patnr;
    return state.setIn(['images', patnr, 'uploading'], true);
}

function uploadPatientPictureSuccess(state: Imm.Map<string, any>, action: UploadPatientPictureAction) {
    const patnr = action.meta.patnr;
    const data = action.meta.imageData;
    return state.setIn(['images', patnr, 'uploading'], false).setIn(['images', patnr, 'data'], data);
}

function uploadPatientPicturefailure(state: Imm.Map<string, any>, action: UploadPatientPictureAction) {
    const patnr = action.meta.patnr;
    return state.setIn(['images', patnr, 'uploading'], false);
}

function deletePatientPictureSuccess(state: Imm.Map<string, any>, action: DeletePatientPictureAction) {
    const patnr = action.meta.patnr;
    return state.deleteIn(['images', patnr]);
}
